/* eslint-disable @next/next/no-img-element */
import React, { memo, useCallback, useEffect, useState } from 'react';
import Div from '@/shared/Div';
import Map from '../Map';
import classNames from 'classnames';
import Button from '@/shared/Button';
import Modal from '@/shared/crisp/Modal';
import InnerImageZoom from 'react-inner-image-zoom';
import CrossIcon from '../Icons/Cross';
import dlv from 'dlv';
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css';
import Swipe from 'swipejs';

type MediaAttributes = {
  url: string;
  name: string;
  alternativeText?: string;
  caption?: string;
  width: number;
  height: number;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  previewUrl?: string;
  provider: string;
  provider_metadata: string;
  folderPath: string;
  createdAt: string;
  updatedAt: string;
  formats?: {
    thumbnail: {
      url: string;
      ext: string;
      hash: string;
      mime: string;
      name: string;
      path?: string;
      size: number;
      width: number;
      height: number;
    };
  };
};

type Media = {
  alt: number;
  attributes: MediaAttributes;
};

export type ProductMediaProps = {
  media: Media[];
  plainName: string;
};

export type ProductMediaPropsExtended = {
  modalImageIndex: number;
  activeImageIndex: number;
  modal: boolean;
  openModal: (index: number, modal: boolean) => void;
  media: Media[];
  width: any;
  className?: string;
};

const ThumbnailImages = (props: ProductMediaPropsExtended) => {
  const { media, modalImageIndex, activeImageIndex, openModal, modal, width, className } = props;
  const renderThumbnail = useCallback(
    ({ id, attributes: { url, name, formats } }: any, index: number) => {
      const isActiveImage = index === (modal ? modalImageIndex : activeImageIndex);
      if (!formats?.thumbnail?.url) return null;
      return (
        <Button
          className={classNames('!inline-block flex-shrink-0 w-auto h-full')}
          theme="none"
          dataTestId={`image-button-${index + 1}`}
          key={id}
          onClick={() => openModal(index, modal)}
        >
          <img
            src={formats.thumbnail.url}
            width={148}
            height={148}
            alt={name}
            className={classNames(
              'h-auto rounded-cxs md:rounded-cs  aspect-square',
              {
                ['border border-blue-500']: isActiveImage,
                'object-contain': !modal,
              },
              width,
            )}
          />
        </Button>
      );
    },
    [activeImageIndex, modal, modalImageIndex, openModal, width],
  );

  return (
    <Map
      className={classNames(
        'hide-scrollbar mt-s4 md:mt-s6 leading-none w-full flex px-s5 gap-4 overflow-x-scroll',
        className,
      )}
      list={media}
      data-testid="media-wrapper"
      render={renderThumbnail}
    />
  );
};

const ProductMedia = ({ media, plainName }: ProductMediaProps) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0); // State to manage active image index inside the modal

  useEffect(() => {
    const slider = document.getElementById('slider');
    if (slider) {
      window.mySwipe = new Swipe(slider);
      window.mySwipe.setup({
        draggable: false,
        callback: (index: number) => {
          setActiveImageIndex(index);
        },
      });
    }
  }, [media]);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [media]);

  const openModal = (index: number, modal: boolean) => {
    if (modal) {
      setModalImageIndex(index);
      setModalOpen(true);
    } else {
      window.mySwipe.slide(index);
      setActiveImageIndex(index);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const closeIcon = (
    <Div className="h-s6 w-s6 bg-cti-on-primary rounded-cs mr-s2 mt-s3" dataTestId={'close-button'}>
      <CrossIcon />
    </Div>
  );

  const renderSwipeItem = useCallback((img: any, index: number) => {
    const { url, alternativeText } = dlv(img, 'attributes');
    return (
      <Div className="h-full object-contain " key={index}>
        <img
          src={url}
          alt={alternativeText}
          loading={index === 0 ? 'eager' : 'lazy'}
          className="h-full object-contain rounded-cxs md:rounded-cs w-full aspect-[1/1] md:aspect-[4/3]"
        />
      </Div>
    );
  }, []);

  return (
    <Div className="items-end flex h-full flex-wrap w-full">
      <Div
        className="px-s5  md:px-s6 flex  items-center md:h-[calc(70vh-8.5rem)] w-full"
        onClick={() => openModal(activeImageIndex, true)}
        dataTestId="image"
      >
        <Div id="slider" className="swipe w-full  h-full">
          <Div className="swipe-wrap h-full">
            <Map list={media} render={renderSwipeItem} />
          </Div>
        </Div>
      </Div>
      {media.length === 1 ? null : (
        <ThumbnailImages
          media={media}
          modalImageIndex={modalImageIndex}
          activeImageIndex={activeImageIndex}
          openModal={openModal}
          modal={false}
          width="w-24 md:w-auto md:h-full"
          className="md:h-[calc(30vh-8.5rem)]"
        />
      )}

      {modalOpen && (
        <Modal
          id="product-media-modal"
          open={modalOpen}
          showHeader={false}
          onClose={closeModal}
          classNames="w-full bottom-0 top-0 fixed md:bottom-auto md:relative shadow-xbold bg-white pb-s9 md:pb-s6 w-full !pb-s5"
          closeIcon={closeIcon}
          width="!max-w-[100%] md:!w-8/12"
          modalClassNames={{
            modal:
              '!p-0 !m-0 !align-bottom md:!align-middle max-h-[99%] min-w-[350px] max-w-[100%] w-[100%] md:w-[auto] md:max-w-2xl rounded-t-cs md:rounded-cs !max-w-[100%] md:!w-8/12',
          }}
        >
          <Div className="h-full pt-s6 overflow-y-auto hide-scrollbar flex flex-col justify-between  mx-auto">
            <Div className="flex justify-end px-s5 md:absolute right-0 z-10">
              <Button theme="none" className="text-black " onClick={closeModal}>
                <CrossIcon fill="#000" />
              </Button>
            </Div>
            <Div className="w-full flex justify-center items-center  pl-s5 pr-s5">
              <InnerImageZoom
                src={media[modalImageIndex].attributes.url}
                zoomSrc={media[modalImageIndex].attributes.url}
                className="object-contain rounded-cxs md:rounded-cs md:h-[calc(100vh-12rem)] w-full mx-auto"
                zoomScale={1}
                fadeDuration={100}
                hideHint={true}
                hideCloseButton={true}
              />
            </Div>
            <ThumbnailImages
              media={media}
              modalImageIndex={modalImageIndex}
              activeImageIndex={activeImageIndex}
              openModal={openModal}
              modal={true}
              width="w-24 !md:w-24"
            />
          </Div>
        </Modal>
      )}
    </Div>
  );
};

export default memo(ProductMedia);
